import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { memoize } from 'proxy-memoize';
import { AppScene, RootState, Scene } from 'types';

export type NavigationStateProps = {
  /** Indicates if the user is currently being authenticated */
  isAuthenticating: boolean;
  /** Indicates if the user is authenticated, successfully or not */
  isAuthenticated: boolean;
  /** Indicates the currently selected scene */
  sceneSelected: Scene;
  /** Indicates if the app has been initialized */
  appInitialized: boolean;
  /** Indicates if the authentication scene/modal should be shown */
  showAuthenticationScene: boolean;
  /** Indicates if there is an ongoing action pertaining to scene change or authentication in progress */
  isLoading: boolean;
};

const initialState: NavigationStateProps = {
  isAuthenticating: false,
  isAuthenticated: false,
  showAuthenticationScene: false,
  sceneSelected: AppScene.None,
  appInitialized: false,
  isLoading: false,
};

export const navigationSlice = createSlice({
  name: 'nav',
  initialState,
  reducers: {
    setBeginAppInitialization: (state) => {
      state.appInitialized = false;
      state.isAuthenticating = true;
      state.isAuthenticated = false;
      state.isLoading = true;
    },
    setEndAppInitialization: (state) => {
      state.appInitialized = true;
      state.isAuthenticating = false;
      state.isAuthenticated = true;
      state.isLoading = false;
    },
    setSelectedScene: (state, action: PayloadAction<Scene>) => {
      state.sceneSelected = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsAuthenticated: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticated = action.payload;
    },
    setIsAuthenticating: (state, action: PayloadAction<boolean>) => {
      state.isAuthenticating = action.payload;
    },
    setShowAuthenticationScene: (state, action: PayloadAction<boolean>) => {
      state.showAuthenticationScene = action.payload;
    },
    setAppInitialized: (state, action: PayloadAction<boolean>) => {
      state.appInitialized = action.payload;
    },
    resetNavigationState: () => {
      return { ...initialState };
    },
  },
});

export const {
  setBeginAppInitialization,
  setEndAppInitialization,
  setSelectedScene,
  setIsLoading,
  setIsAuthenticated,
  setIsAuthenticating,
  setShowAuthenticationScene,
  setAppInitialized,
  resetNavigationState,
} = navigationSlice.actions;

// #region UI Selectors *********************************************************
export const getSelectedScene = (state: RootState): Scene => state.nav.sceneSelected;
export const getIsAppInitialized = (state: RootState): boolean => !!state.nav.appInitialized;
export const getIsLoading = (state: RootState): boolean => state.nav.isLoading;
export const getIsAuthenticated = (state: RootState): boolean => state.nav.isAuthenticated;
export const getIsAuthenticating = (state: RootState): boolean => state.nav.isAuthenticating;
export const getShowAuthenticationScene = (state: RootState): boolean => state.nav.showAuthenticationScene;

// #region HELPER SELECTORS FOR SPECIFIC SCENES *******************************
export const getIsUserInAuthenticationScene = memoize((state: RootState): boolean => {
  return state.nav.sceneSelected === AppScene.Authentication;
});
export const getIsUserInLandingScene = memoize((state: RootState): boolean => {
  return state.nav.sceneSelected === AppScene.Landing;
});
export const getIsUserInWeeklyGamesScene = memoize((state: RootState): boolean => {
  return state.nav.sceneSelected === AppScene.WeeklyGames;
});
export const getIsUserInTeamDetailsScene = memoize((state: RootState): boolean => {
  return state.nav.sceneSelected === AppScene.TeamDetails;
});
export const getIsUserInGameDetailsScene = memoize((state: RootState): boolean => {
  return state.nav.sceneSelected === AppScene.GameDetails;
});
export const getIsUserInStandingsScene = memoize((state: RootState): boolean => {
  return state.nav.sceneSelected === AppScene.Standings;
});
export const getIsUserInTeamsScene = memoize((state: RootState): boolean => {
  return state.nav.sceneSelected === AppScene.Teams;
});

// #endregion

// #endregion

export default navigationSlice.reducer;
